import { useMemo } from 'react';

import { useIsAbTestAvailable } from 'src/hooks/useIsAbTestAvailable';
import { useListAbTestConfigsQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import { SolveABTestConfig } from 'src/services/agent-ab-test/types';
import { ABTestVariantInfo } from 'src/services/insights/types';

export function variantLookupKey(version: number, versionType: string): string {
  return `${version}:${versionType}`;
}

/**
 * Build a lookup map from variantLookupKey to ABTestVariantInfo
 * for all AB test configs that match the given workflow.
 */
export function buildVariantLookup(
  configs: SolveABTestConfig[],
  intentWorkflowId: string,
): Map<string, ABTestVariantInfo> {
  const map = new Map<string, ABTestVariantInfo>();
  for (const config of configs) {
    if (config.intent_workflow_id !== intentWorkflowId) {
      continue;
    }
    for (const variant of config.variants ?? []) {
      const key = variantLookupKey(
        variant.intent_workflow_version,
        variant.version_type,
      );
      map.set(key, {
        ab_test_config_id: config.ab_test_config_id,
        ab_test_name: config.name,
        ab_test_status: config.status,
        end_condition_type: config.end_condition?.condition_type ?? null,
        end_date: config.end_condition?.end_datetime ?? null,
        intent_workflow_version: variant.intent_workflow_version,
        is_control: variant.is_control,
        sample_count: config.end_condition?.sample_count ?? null,
        start_date: config.start_datetime ?? null,
        variant_name:
          variant.name ?? (variant.is_control ? 'Control' : 'Variant'),
        version_type: variant.version_type,
      });
    }
  }
  return map;
}

/**
 * Hook that returns a variant lookup map for the given workflow,
 * backed by the AB test configs list query.
 */
export function useVariantLookup(intentWorkflowId: string) {
  const isAbTestAvailable = useIsAbTestAvailable();
  const { data: abTestConfigsData } = useListAbTestConfigsQuery(
    {
      page: 1,
      pageSize: 100,
    },
    { skip: !isAbTestAvailable },
  );

  return useMemo(
    () =>
      buildVariantLookup(
        abTestConfigsData?.ab_test_configs ?? [],
        intentWorkflowId,
      ),
    [abTestConfigsData, intentWorkflowId],
  );
}
